import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getLearnerPersianFontStack,
  isLearnerPersianFontId,
  LEARNER_SETTINGS_MODAL_CLOSED_EVENT,
  type LearnerPersianFontId
} from '../constants/learnerPersianFonts';
import { applyLearnerPersianFont, readStoredLearnerPersianFont } from '../utils/applyLearnerPersianFont';
import { authService } from '../services/authService';
import { useAuth } from './useAuth';
import { useFontSize } from '../components/learning/FontSizeContext';

export type LessonTypographyScope = { stack: string; multiplier: number };

/**
 * Preferences applied only to lesson Text / Text+Audio blocks (not global UI).
 */
export function useLessonTypographyPreferences(): LessonTypographyScope {
  const { user } = useAuth();
  const { currentMultiplier, setFontSize } = useFontSize();
  const [lessonFontId, setLessonFontId] = useState<LearnerPersianFontId>(() => readStoredLearnerPersianFont());

  const refreshFromServer = useCallback(async () => {
    if (!user?.id) return;
    try {
      const p = await authService.getLearningPreferences();
      const f = p.preferredPersianFontId?.trim().toLowerCase();
      if (f && isLearnerPersianFontId(f)) {
        setLessonFontId(f);
      } else {
        setLessonFontId(readStoredLearnerPersianFont());
      }
      const m = p.lessonTextFontMultiplier;
      if (typeof m === 'number' && Number.isFinite(m)) {
        setFontSize(m);
      }
    } catch {
      /* offline */
    }
  }, [user?.id, setFontSize]);

  useEffect(() => {
    void refreshFromServer();
  }, [refreshFromServer]);

  /** After quick settings modal closes (e.g. on a lesson), sync stack from storage + server so Text blocks update. */
  useEffect(() => {
    const onModalClosed = () => {
      const id = readStoredLearnerPersianFont();
      applyLearnerPersianFont(id);
      setLessonFontId(id);
      void refreshFromServer();
    };
    window.addEventListener(LEARNER_SETTINGS_MODAL_CLOSED_EVENT, onModalClosed);
    return () => window.removeEventListener(LEARNER_SETTINGS_MODAL_CLOSED_EVENT, onModalClosed);
  }, [refreshFromServer]);

  return useMemo(
    () => ({
      stack: getLearnerPersianFontStack(lessonFontId),
      multiplier: currentMultiplier
    }),
    [lessonFontId, currentMultiplier]
  );
}
